import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Shield, EnvelopeSimple, CurrencyDollar, ArrowsClockwise, CheckCircle, XCircle, Receipt, MagnifyingGlass } from "@phosphor-icons/react";

function fmtMoney(n) {
  return Number(n || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " $";
}
function fmtDate(s) {
  if (!s) return "—";
  return s.slice(0, 10);
}

export default function BouclierFiscalModule() {
  const [data, setData] = useState({ contacts: [], total_threads: 0 });
  const [busy, setBusy] = useState(false);
  const [paymentCheck, setPaymentCheck] = useState({ amount: "1753.94", result: null, loading: false });
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/bouclier-fiscal/threads");
      setData(r.data);
    } catch {
      toast.error("Impossible de charger");
    }
  };

  useEffect(() => { load(); checkPayment(); }, []);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post("/bouclier-fiscal/refresh");
      if (r.data.success) {
        toast.success("Scan Gmail terminé");
        await load();
      } else {
        toast.error(`Erreur: ${r.data.error || "inconnu"}`);
      }
    } catch (e) {
      toast.error("Refresh échoué");
    }
    setBusy(false);
  };

  const checkPayment = async () => {
    if (!paymentCheck.amount) return;
    setPaymentCheck(p => ({ ...p, loading: true }));
    try {
      const r = await api.get(`/bouclier-fiscal/payment-status?amount=${paymentCheck.amount}`);
      setPaymentCheck(p => ({ ...p, result: r.data, loading: false }));
    } catch {
      setPaymentCheck(p => ({ ...p, loading: false }));
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="bouclier-fiscal-module">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Shield size={28} weight="bold" className="text-[#D90429]" />
            Bouclier Fiscal
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Tous tes échanges avec comptables et fiscalistes — Brent Vaillancourt, Catherine Fuchs (Bisso Services), L'Équipe Tremblay.
            Vérification automatique des paiements vs tes transactions bancaires.
          </p>
        </div>
        <button
          data-testid="btn-refresh-bouclier"
          onClick={refresh}
          disabled={busy}
          className="px-4 py-2 hard-border hard-border-active text-sm font-semibold flex items-center gap-2 hover:bg-muted disabled:opacity-50"
        >
          <ArrowsClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} />
          {busy ? "Scan en cours…" : "Re-scanner Gmail"}
        </button>
      </div>

      {/* Vérificateur de paiement */}
      <div className="hard-border p-4 bg-secondary/20" data-testid="payment-checker">
        <div className="flex items-center gap-2 mb-3">
          <MagnifyingGlass size={16} weight="bold" className="text-[#D90429]" />
          <h2 className="font-semibold">Vérificateur de paiement</h2>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <input
            data-testid="input-payment-amount"
            type="number"
            step="0.01"
            value={paymentCheck.amount}
            onChange={(e) => setPaymentCheck(p => ({ ...p, amount: e.target.value }))}
            placeholder="Montant en $"
            className="px-3 py-2 hard-border text-sm w-40 bg-background"
          />
          <button
            data-testid="btn-check-payment"
            onClick={checkPayment}
            disabled={paymentCheck.loading}
            className="px-3 py-2 bg-foreground text-background text-sm font-semibold hard-border hover:opacity-90 disabled:opacity-50"
          >
            {paymentCheck.loading ? "…" : "Vérifier"}
          </button>
          {paymentCheck.result && (
            <div className="flex items-center gap-2 text-sm">
              {paymentCheck.result.is_paid ? (
                <span className="flex items-center gap-1 text-emerald-500 font-semibold">
                  <CheckCircle weight="fill" size={18} /> PAYÉ — {paymentCheck.result.matches_count} match
                </span>
              ) : (
                <span className="flex items-center gap-1 text-amber-500 font-semibold">
                  <XCircle weight="fill" size={18} /> NON PAYÉ
                </span>
              )}
            </div>
          )}
        </div>
        {paymentCheck.result?.matches?.length > 0 && (
          <div className="mt-3 space-y-1">
            {paymentCheck.result.matches.slice(0, 3).map((m, i) => (
              <div key={i} className="text-xs text-muted-foreground">
                {fmtDate(m.date)} · <strong>{fmtMoney(m.amount)}</strong> · {m.type} ({m.account}) · {(m._supplier || m.description || "").slice(0, 60)}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Contacts */}
      <div className="space-y-4" data-testid="contacts-list">
        <h2 className="text-lg font-bold tracking-tight">Contacts ({data.contacts.length})</h2>
        {data.contacts.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-8 hard-border">
            Aucun thread indexé. Clique sur "Re-scanner Gmail".
          </div>
        )}
        {data.contacts.map((c) => (
          <div key={c.contact_email} className="hard-border" data-testid="contact-card">
            <div
              className="p-4 flex items-center gap-3 cursor-pointer hover:bg-secondary/20"
              onClick={() => setExpanded(expanded === c.contact_email ? null : c.contact_email)}
            >
              <div className="w-10 h-10 hard-border bg-foreground text-background flex items-center justify-center font-bold">
                {c.contact_name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{c.contact_name}</div>
                <div className="text-xs text-muted-foreground truncate">{c.contact_email}</div>
              </div>
              <div className="flex items-center gap-3 text-sm flex-wrap">
                <div className="flex items-center gap-1">
                  <EnvelopeSimple size={14} className="text-muted-foreground" />
                  <span className="font-bold">{c.thread_count}</span>
                </div>
                {c.amounts_unique?.length > 0 && (
                  <div className="flex items-center gap-1">
                    <CurrencyDollar size={14} className="text-emerald-500" />
                    <span className="font-bold">{fmtMoney(c.amounts_unique[0])}</span>
                    <span className="text-xs text-muted-foreground">(max)</span>
                  </div>
                )}
                {c.invoices_unique?.length > 0 && (
                  <div className="flex items-center gap-1">
                    <Receipt size={14} className="text-muted-foreground" />
                    <span className="text-xs">{c.invoices_unique.length} fact.</span>
                  </div>
                )}
              </div>
            </div>
            {expanded === c.contact_email && (
              <div className="border-t border-foreground/20 p-4 space-y-3 bg-secondary/10">
                {c.threads.map((t, i) => (
                  <div key={i} className="text-sm hard-border p-3 bg-background">
                    <div className="flex justify-between items-start gap-2 flex-wrap">
                      <div className="font-semibold flex-1">{t.subject || "(sans objet)"}</div>
                      <div className="text-xs text-muted-foreground whitespace-nowrap">{t.date?.slice(0, 25)}</div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{t.from}</div>
                    {t.snippet && (
                      <div className="text-xs mt-2 text-muted-foreground italic line-clamp-2">{t.snippet}</div>
                    )}
                    {(t.amounts_found?.length > 0 || t.invoices_found?.length > 0) && (
                      <div className="flex gap-4 mt-2 text-xs">
                        {t.amounts_found?.length > 0 && (
                          <div>
                            <span className="font-semibold">Montants:</span>{" "}
                            {t.amounts_found.slice(0, 3).map(a => fmtMoney(a)).join(" · ")}
                          </div>
                        )}
                        {t.invoices_found?.length > 0 && (
                          <div>
                            <span className="font-semibold">Factures:</span>{" "}
                            {t.invoices_found.join(", ")}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
